import React, { Component } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import AdrLocWorkPerformedRowV3 from "./AdrLocWorkPerformedRowV3";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import adrActions from "../../_actions/adrActions";
import {
  wpIsItem,
  wpIsCco,
  wpIsPendingIssue,
  getLocationOption,
  getSublocationOption,
  getNewLocWpr,
  getSpecialLocationOptionValue,
} from "./utils/adrUtilsV3.js";
import { ADR_EDIT } from "../../_constants/actionConstants";
import _ from "lodash";
import { SUBLOCATION_WORKPERFORMED_TYPE } from "./constants/adrConstantsV3";
import memoizeOne from "memoize-one";

class AdrLocWorkPerformedV3 extends Component {
  state = {
    /** @type {number[]} */
    locationsToDisable: [],
    showAlert_noMoreLocations: false, // No longer used
  };

  componentDidUpdate() {
    const { workPerformedClient: currWp } = this.props;
    if (currWp.locationWorkPerformed.length === 0) this.addGenLoc();
  }

  addGenLoc = () => {
    const { dispatch, tabId, wpTabId } = this.props;
    const loadWithGenLoc = true;
    const newLocWp = this.loadNewLocWpr(loadWithGenLoc);
    if (newLocWp) {
      dispatch({
        type: ADR_EDIT.LOCATION_WORKPERFORMED.ADD,
        tabId,
        wpTabId,
        newLocWp,
      });
    }
  };

  /**
   * Returns a new LocationWorkPerformedRow.
   * @param {bool} loadWithGenLoc -
   */
  loadNewLocWpr = (loadWithGenLoc = false) => {
    const { projLocationList, workPerformedClient: wpc } = this.props;

    // Create a new locWpr from blank template. To be modified below as needed
    // prior to returning.
    let newLocWpr = getNewLocWpr();

    if (loadWithGenLoc) {
      const genLoc = (projLocationList || []).find((l) => l.isGeneric);
      newLocWpr = { ...newLocWpr, locationId: genLoc ? genLoc.locationId : 0 };
    }

    return newLocWpr;
  };

  handleClick_addLoc = (e) => {
    const {
      dispatch,
      tabId,
      wpTabId,
      markAdrDirty,
      workPerformedClient: wpc,
    } = this.props;
    e.preventDefault();

    const newLocWp = this.loadNewLocWpr();
    dispatch({ type: ADR_EDIT.LOCATION_WORKPERFORMED.ADD, tabId, wpTabId, newLocWp });
    markAdrDirty();
  };

  toggleAlert_noMoreLocations = () =>
    this.setState({ showAlert_noMoreLocations: !this.state.showAlert_noMoreLocations });

  handleAlert_noMoreLocations = () => (e) => {
    e.preventDefault();
    this.toggleAlert_noMoreLocations();
  };

  getTabPaneClass = () => {
    const { selectedWpTabId, workPerformedClient: wpc } = this.props;
    return selectedWpTabId === wpc.wpTabId
      ? "tab-pane fade show active"
      : "tab-pane fade";
  };

  /**
   * Notes on locationOptions:
   * =========================
   * - Memoize locationOptions so its creation is only dependent on the changes in its
   *   feeding location lists, and not on every component re-render (which may or may
   *   not be due to the changes in feeding location lists.)
   * - The order of locationOptions always follows the same pattern: #1 is generic location,
   *   #2 is "Line-Sta", #3 is separator ("-----"), and #4 and on are the actual locations.
   *   If no locations are associated with the workPerformed's item/CCO/PI, then locationOptions
   *   only contains #1 and #2.
   * - 8/4/25 UPDATE Allow selection of all locations set up in ProjAdmin Locations tab.
   *   Disable locations by item/CCO/PI
   */
  memoizedLocationOptions = memoizeOne((projLocationList) => {
    // Get generic location (always first)
    const genericLoc = (projLocationList || []).find(l => l.isGeneric);
    const genericOption = genericLoc ? getLocationOption(genericLoc, 0) : null;

    // Special options
    const specialOptions = [
      {
        label: "Line-Sta.",
        value: getSpecialLocationOptionValue(SUBLOCATION_WORKPERFORMED_TYPE.ALIGNMENT),
        order: 1
      },
      {
        label: "-----------",
        value: -10,
        order: 2
      }
    ];

    // All regular locations (excluding generic and alignment typeId = 15)
    const locationOptions = (projLocationList || [])
      .filter(l => !l.isGeneric && l.locationTypeId !== 15)  // Explicitly filter out typeId 15
      .map((location, index) => getLocationOption(location, index + 3));

    // Combine all options in desired order
    const allOptions = [
      ...(genericOption ? [genericOption] : []),
      ...specialOptions,
      ...locationOptions
    ];

    return _.orderBy(allOptions, "order", "asc");
  });

  /**
   * Returns sublocation options for the selected locWp.locationId.
   * Note: locationOptions for the location selector are provided as props
   * to this component.
   * @returns {SelectOption[]}
   */
  memoizedSublocationOptions = memoizeOne((projLocationList, locWp) => {
    if (!locWp.locationId) return null;
    // If the selected location is a 'special' sublocation, then generate
    // the list specific to each type of the sublocation (e.g. alignment,
    // coordinate, etc.)
    if (locWp.locationId < 0) {
      switch (locWp.locationId) {
        case -2: {
          const { locationTypeList } = this.props;
          const alignLocType = (locationTypeList || []).find(
            (lt) => lt.description === "Alignment"
          );
          // -2 = Line/Sta: get list of alignments
          return _.orderBy(
            (projLocationList || [])
              .filter(
                (l) => alignLocType && l.locationTypeId === alignLocType.locationTypeId
              )
              .map((l) => getLocationOption(l)),
            "label",
            "asc"
          );
        }
        default:
          return null;
      }
    } else {
      // If the selected location is a regular location, generate a list
      // of nested sublocations
      const currLoc = (projLocationList || []).find(
        (l) => l.locationId === locWp.locationId
      );
      return currLoc
        ? currLoc.sublocations
          ? currLoc.sublocations.map((sl) => getSublocationOption(sl))
          : null
        : null;
    }
  });

  renderLocWpr = (locWp) => {
    const {
      projectId,
      tabId,
      wpTabId,
      workPerformedClient: wpc,
      markAdrDirty,
      fileUploadModal,
      status,
      projLocationList,
      projItemList,
      projCcoList,
      projPendingIssueLocationList,
    } = this.props;

    const locationOptions = this.memoizedLocationOptions(
      projLocationList,
      projItemList,
      projCcoList,
      projPendingIssueLocationList,
      wpc
    );

    const sublocationOptions = this.memoizedSublocationOptions(projLocationList, locWp);

    return (
      <AdrLocWorkPerformedRowV3
        key={`locWprKey_${locWp.clientId}`}
        projectId={projectId}
        locationWorkPerformed={locWp}
        tabId={tabId}
        wpTabId={wpTabId}
        locationOptions={locationOptions}
        sublocationOptions={sublocationOptions}
        locationsToDisable={wpc.locationWorkPerformed
          .filter((locWp) => locWp.locationId && locWp.locationId > 0)
          .map((locWp) => locWp.locationId)}
        unitOfMeasure={wpc.unitOfMeasure}
        markAdrDirty={markAdrDirty}
        fileUploadModal={fileUploadModal}
        status={status}
      />
    );
  };

  renderLocWpTab = () => {
    const { tabId, wpTabId, workPerformedClient: wpc, status } = this.props;
    const locWpSo = adrActions.getLocWorkPerformedSortOrder(tabId, wpTabId);
    if (locWpSo.length === 0) {
    }
    return (
      <React.Fragment>
        <label className="loc-wp-item-caption">
          <em>{wpc.wpFullCaption}</em>
        </label>
        <hr className="mt-0 mb-2" />
        {wpc.locationWorkPerformed.map((locWp) => this.renderLocWpr(locWp))}
        <div className="form-row">
          <div className="col-md-5">
            <button
              className="btn btn-secondary"
              id={`addWprButton_${wpTabId}`}
              onClick={this.handleClick_addLoc}
              disabled={status.isReadOnly}
            >
              Add New Location
            </button>
          </div>
        </div>
      </React.Fragment>
    );
  };

  renderIdleTab = () => {
    return (
      <React.Fragment>
        <label>
          <em>{this.props.workPerformedClient.wpFullCaption}</em>
          <span className="badge badge-danger ml-2">Idle</span>
        </label>
        <hr className="mt-0 mb-1" />
        <div className="alert alert-warning">
          <h5>
            <strong>This item/CCO is added as 'idle'.</strong>
          </h5>
          <p>
            For location-specific work description or quantity inputs, add the same item
            and leave "Add as idle" unchecked. Idle hours can be entered only for
            equipment.
          </p>
          <p className="mb-1">
            To add comments related to the idle equipment, click or tap "
            <FontAwesomeIcon icon="ellipsis-h" />" button towards the right end of each
            labor row.
          </p>
          <p>
            Closing this tab will delete the corresponding idle item column in Labor and
            Equipment tables below.
          </p>
        </div>
      </React.Fragment>
    );
  };

  render() {
    const { selectedWpTabId, wpTabId, workPerformedClient: wpc } = this.props;
    return (
      <React.Fragment>
        <div
          className={
            selectedWpTabId === wpTabId
              ? "tab-pane show active border border-secondary p-4"
              : "tab-pane border border-secondary p-4"
          }
          id={wpTabId}
          role="tabpanel"
        >
          {!wpc.isIdle ? this.renderLocWpTab() : this.renderIdleTab()}
        </div>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state, ownProps) => ({
  projLocationList: state.locations.projLocationLists[ownProps.projectId] || null,
  locationTypeList: state.feederLists.locationTypeList || null,
  projItemList: state.bidItems.projItemLists[ownProps.projectId] || null,
  projCcoList: state.ccos.projCcoLists[ownProps.projectId] || null,
  projPendingIssueList:
    state.pendingIssues.projPendingIssueLists[ownProps.projectId] || null,
});

AdrLocWorkPerformedV3.propTypes = {
  projectId: PropTypes.number,
  tabId: PropTypes.number,
  wpTabId: PropTypes.number,
  selectedWpTabId: PropTypes.number,
};

export default connect(mapStateToProps)(AdrLocWorkPerformedV3);
